import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { TextField, Grid, Button, Typography, FormControlLabel, Switch, Box, CircularProgress } from '@material-ui/core';
import InputMask from 'react-input-mask';

import TituloPagina from '../../components/layout/TituloPagina';
import If from '../../components/condicional/if';
import { editFuncionario, getFuncionario } from '../../store/actions/funcionario';
import DialogAlert from '../../components/layout/DialogAlert';
import converterData from '../../assets/js/converterData';
import LoadingOverlay from '../../components/layout/LoadingOverlay';

const FuncionarioEditar = props => {
    const history = useHistory();
    const location = useLocation();
    const { funcionario, editFuncionario, getFuncionario, loading, user } = props;
    const { perfis } = user || {};
    const [disableFields, setDisableFields] = useState(false);
    const [toggleDialog, setToggleDialog] = useState(false);
    const [mensagemErro, setMensagemErro] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingMessage, setProcessingMessage] = useState('');
    const isPlatformAdmin = Array.isArray(perfis) && perfis.includes('ADMIN_APP');
    const from = location.state?.from || '/funcionarios';
    const id = Number(location?.state?.funcionarioId);
    const isEditRoute = location.pathname === '/funcionarios-da-loja/editar-funcionario';

    useEffect(() => {
        if (isEditRoute && !id) {
            if (isPlatformAdmin) {
                history.replace('/funcionarios');
            } else {
                history.replace('/funcionarios-da-loja');
            }
            return;
        }

        if (id > 0) {
            getFuncionario(id);
        }
    }, [getFuncionario, history, location]);

    const validationSchema = yup.object({
        matricula: yup.string()
            .required('O Número de Identificação Funcional é obrigatório'),
    });

    const formik = useFormik({
        initialValues: {
            id: funcionario?.id ?? '',
            matricula: funcionario?.matricula ?? '',
            dataAdmissao: funcionario?.dataAdmissao ?? '',
            dataDesligamento: funcionario?.dataDesligamento ?? '',
            ativo: funcionario?.ativo ?? false,
            idNegocio: funcionario?.idNegocio ?? '',
            nomeNegocio: funcionario?.nomeNegocio ?? ''
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {

            setProcessingMessage('Salvando...');
            setIsProcessing(true);

            const data = {
                ...values,
                dataAdmissao: converterData(values.dataAdmissao),
                dataDesligamento: converterData(values.dataDesligamento)
            };
            editFuncionario(data);

            setIsProcessing(false);
            setProcessingMessage('');
            setDisableFields(true);
        },
        enableReinitialize: true,
    });

    function handleDialogError() {
        setToggleDialog(false);
        setMensagemErro('');
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <>
            <TituloPagina titulo='Cadastro de Funcionário' subtitulo={new Date().toLocaleString('pt-BR')} />
            <Grid container item xs={12}>
                <Grid container style={{ paddingTop: '15px', paddingBottom: '15px' }}>
                    <Typography variant='body1' component='p'>
                        Dados no negócio
                    </Typography>
                </Grid>

                <div style={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                fullWidth
                                id='nomeNegocio'
                                name='nomeNegocio'
                                label='Nome do Negócio'
                                variant='filled'
                                disabled={disableFields}
                                value={funcionario?.nomeNegocio ?? ''}
                            />
                        </Grid>
                    </Grid>
                </div>

                <Grid container style={{ paddingTop: '15px', paddingBottom: '15px' }}>
                    <Typography variant='body1' component='p'>
                        Dados do funcionário
                    </Typography>
                </Grid>

                <div style={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                fullWidth
                                id='nome'
                                name='nome'
                                label='Nome'
                                variant='filled'
                                disabled={disableFields}
                                value={funcionario?.nomeCompleto ?? ''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                id='cpf'
                                name='cpf'
                                label='CPF'
                                variant='filled'
                                disabled={disableFields}
                                value={funcionario?.cpf ?? ''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                id='dataNascimento'
                                name='dataNascimento'
                                label='Data de Nascimento'
                                variant='filled'
                                disabled={disableFields}
                                value={funcionario?.dataNascimento ?? ''}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                id='email'
                                name='email'
                                label='E-mail'
                                variant='filled'
                                disabled={disableFields}
                                value={funcionario?.email ?? ''}
                            />
                        </Grid>
                    </Grid>
                </div>

                <form onSubmit={formik.handleSubmit} noValidate autoComplete='off' style={{ width: 'inherit' }}>
                    <div style={{ flexGrow: 1, paddingTop: '15px' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='matricula'
                                    name='matricula'
                                    label='Número de Identificação Funcional'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.matricula}
                                    onChange={formik.handleChange}
                                    error={formik.touched.matricula && Boolean(formik.errors.matricula)}
                                    helperText={formik.touched.matricula && formik.errors.matricula} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={'99/99/9999'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.dataAdmissao}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='dataAdmissao'
                                        name='dataAdmissao'
                                        label='Data de Admissão'
                                        variant='filled'
                                        error={formik.touched.dataAdmissao && Boolean(formik.errors.dataAdmissao)}
                                        helperText={formik.touched.dataAdmissao && formik.errors.dataAdmissao}
                                    />}
                                </InputMask>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={'99/99/9999'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.dataDesligamento}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='dataDesligamento'
                                        name='dataDesligamento'
                                        label='Data de Desligamento'
                                        variant='filled'
                                        error={formik.touched.dataDesligamento && Boolean(formik.errors.dataDesligamento)}
                                        helperText={formik.touched.dataDesligamento && formik.errors.dataDesligamento}
                                    />}
                                </InputMask>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Switch
                                        checked={formik.values.ativo}
                                        onChange={formik.handleChange}
                                        name="ativo"
                                        disabled={disableFields} />}
                                    label="Funcionário Ativo"
                                />
                            </Grid>
                        </Grid>
                        <Grid container pt={4} style={{ paddingTop: '25px', paddingBottom: '25px' }}>
                            <If test={!disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button type='submit' color='secondary' variant='contained'>Salvar</Button>
                                </Grid>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button variant='contained' href={from}>Cancelar</Button>
                                </Grid>
                            </If>
                            <If test={disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button color='secondary' variant='contained' href={from}>Voltar</Button>
                                </Grid>
                            </If>
                        </Grid>
                    </div>
                </form>
            </Grid>

            <DialogAlert
                title='Atenção'
                okButton={true}
                labelOkButton='Ok'
                okButtonClick={handleDialogError}
                openDialog={toggleDialog}>
                {mensagemErro}
            </DialogAlert>

            {/* O componente Loading fica ouvindo o estado isProcessing */}
            <LoadingOverlay
                open={isProcessing}
                message={processingMessage}
            />
        </>
    )
}

const mapStateToProps = state => ({
    funcionario: state.funcionario.funcionario,
    loading: state.funcionario.loading
});

const mapDispatchToProps = dispatch => bindActionCreators({
    editFuncionario,
    getFuncionario
}, dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(FuncionarioEditar);